import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useClassName, useScrollBlock, useWindowSize } from 'common/hooks';
import { ContentfulRichText, SVGIcon } from 'common/components';

import {
  getAssetUrl,
  getResponsiveAsset,
  isVideoAsset,
} from 'public/helpers/contentful/utils';

import FloatingWidgetCTA from './FloatingWidgetCTA';
import { EXIT_DURATION, PHASES } from './utils';

function CloseButton({ className, label, onClick }) {
  return (
    <button
      type="button"
      className={className}
      aria-label={label}
      onClick={onClick}>
      <SVGIcon name="close" size="mini" />
    </button>
  );
}

CloseButton.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  onClick: PropTypes.func,
};

export default function FloatingWidget({
  entry,
  phase,
  onExpand,
  onCollapse,
  onDismiss,
}) {
  const classNames = useClassName('FloatingWidget');
  const viewport = useWindowSize();
  const [blockScroll, allowScroll] = useScrollBlock();

  const fields = entry?.fields;
  const isVisible =
    Boolean(fields) &&
    (phase === PHASES.collapsed || phase === PHASES.expanded);

  const [isMounted, setIsMounted] = useState(isVisible);
  const lastVisiblePhase = useRef(phase);
  const videoRef = useRef(null);

  const activePhase = isVisible ? phase : lastVisiblePhase.current;
  const isExpanded = activePhase === PHASES.expanded;

  const isSheetOpen =
    isVisible && phase === PHASES.expanded && viewport.isMobile;

  useEffect(() => {
    if (!isSheetOpen) return undefined;
    blockScroll();
    return allowScroll;
  }, [isSheetOpen]);

  useEffect(() => {
    if (isVisible) {
      lastVisiblePhase.current = phase;
      setIsMounted(true);
      return undefined;
    }

    const timer = setTimeout(() => setIsMounted(false), EXIT_DURATION);

    return () => clearTimeout(timer);
  }, [isVisible, phase]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isExpanded) {
      video.play().catch(() => {});
      return;
    }

    video.pause();
    video.currentTime = 0;
  }, [isExpanded, isMounted]);

  if (!fields || !isMounted) return null;

  const { title, description, collapsedCta, expandedCta, backgroundAsset } =
    fields;

  const background = getResponsiveAsset(backgroundAsset, viewport);
  const backgroundUrl = getAssetUrl(background);
  const isBackgroundVideo = Boolean(backgroundUrl) && isVideoAsset(background);
  const hasExpandedCta = Boolean(expandedCta?.fields?.label);

  const layerClassName = (name, isActive) => {
    return [classNames('layer', isActive ? 'active' : ''), classNames(name)]
      .filter(Boolean)
      .join(' ');
  };

  return (
    <div
      className={classNames('wrapper', isExpanded ? 'expanded' : 'collapsed')}>
      <div className={classNames('stage', isVisible ? '' : 'leaving')}>
        {hasExpandedCta && (
          <div
            aria-hidden="true"
            className={classNames('scrim', isExpanded ? 'active' : '')}
            onClick={onCollapse}
          />
        )}
        <div className={layerClassName('pill', !isExpanded)}>
          <FloatingWidgetCTA
            cta={collapsedCta}
            phase={activePhase}
            className={classNames('collapsed-cta')}
            onClick={(event) => {
              if (!hasExpandedCta) {
                onDismiss();
                return;
              }

              event.preventDefault();
              onExpand();
            }}
          />
          <CloseButton
            className={classNames('close', 'collapsed')}
            label="Dismiss"
            onClick={onDismiss}
          />
        </div>
        {hasExpandedCta && (
          <div className={layerClassName('card', isExpanded)}>
            <div
              className={classNames('media')}
              style={{
                '--floating-widget-background':
                  backgroundUrl && !isBackgroundVideo
                    ? `url(${backgroundUrl})`
                    : 'none',
              }}>
              {isBackgroundVideo && (
                <video
                  ref={videoRef}
                  className={classNames('media-video')}
                  src={backgroundUrl}
                  loop
                  muted
                  playsInline
                  preload="metadata"
                />
              )}
              <CloseButton
                className={classNames('close', 'expanded')}
                label="Collapse"
                onClick={onCollapse}
              />
            </div>
            <div className={classNames('content')}>
              {Boolean(title) && (
                <h3 className={classNames('title')}>{title}</h3>
              )}
              {Boolean(description) && (
                <div className={classNames('description')}>
                  <ContentfulRichText value={description} />
                </div>
              )}
              <FloatingWidgetCTA
                cta={expandedCta}
                phase={activePhase}
                className={classNames('expanded-cta')}
                onClick={onDismiss}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

FloatingWidget.propTypes = {
  entry: PropTypes.object,
  phase: PropTypes.string,
  onExpand: PropTypes.func,
  onCollapse: PropTypes.func,
  onDismiss: PropTypes.func,
};
